import {
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter, Routes } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { provideTranslateService } from '@ngx-translate/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { LanguageInitService } from './services/language-init.service';
import { PrimeNGTranslationService } from './services/primeng-translation.service';
import { PermissionService } from './services/permission.service';
import { KeycloakService } from './services/keycloak.service';
import {
  provideHttpClient,
  HTTP_INTERCEPTORS,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { DialogService } from 'primeng/dynamicdialog';
import { provideApi as provideGoodsApi } from '@app/api';
import { provideApi as provideAuthApi } from '@app/api/authen-service';
import { provideApi as provideGoodsApi2 } from '@app/api/goods-service';
import { provideApi as provideOrderApi } from '@app/api/order-service';
import { provideApi as provideStockApi } from '@app/api/stock-service';
import { provideApi as provideChannelApi } from '@app/api/channel-service';
import { provideApi as providePosApi } from '@app/api/pos-service';
import { provideApi as providePromotionApi } from '@app/api/promotion-service';
import { provideApi as providePaymentApi } from '@app/api/payment-service';
import { provideApi as providePurchaseApi } from '@app/api/purchase-service';
import { provideApi as provideVendorApi } from '@app/api/vendor-service';
import { provideApi as provideUserApi } from '@app/api/user-service';
import { provideApi as provideAfterSalesApi } from '@app/api/after-sales-service';
import { provideApi as provideEinvoiceApi } from '@app/api/einvoice-service';
import { provideApi as providePriceApi } from '@app/api/price-service';
import { environment } from '@environments/environment';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { initializeKeycloak, getKeycloakInstance } from './auth/keycloak-init';

// Export theme service for easy import
export {
  ThemeService,
  type Theme,
  type ThemeColors,
  THEMES,
} from './services/theme';

export interface CoreOptions {
  routes: Routes;
}

// Language initialization function
export function initializeLanguage() {
  const languageInitService = inject(LanguageInitService);
  return languageInitService.initializeLanguage();
}

// PrimeNG translation initialization function
export function initializePrimeNGTranslations() {
  const primengTranslationService = inject(PrimeNGTranslationService);
  return (async () => {
    await primengTranslationService.initializeTranslations();
    primengTranslationService.subscribeToLanguageChanges();
  })();
}

// Permission initialization function - NON-BLOCKING
export function initPermissions() {
  const permissionService = inject(PermissionService);
  const keycloakService = inject(KeycloakService);

  // Start permission loading in the background without blocking app startup
  const startPermissionLoading = () => {
    // Use cached permissions immediately if available (already checked in loadPermissions)
    // Then refresh from API in background
    permissionService.loadPermissions().subscribe({
      next: () => {
        console.debug(
          '[AppInit] Permissions loaded successfully in background',
        );
      },
      error: (error) => {
        console.error(
          '[AppInit] Failed to load permissions in background:',
          error,
        );
      },
    });
  };

  // Check if we can start loading immediately or need to wait for Keycloak
  if (keycloakService.isReady() && keycloakService.isAuthenticated()) {
    // Keycloak already ready, start loading immediately
    startPermissionLoading();
  } else {
    // Wait for Keycloak to be ready before loading permissions
    const checkKeycloak = () => {
      if (keycloakService.isReady() && keycloakService.isAuthenticated()) {
        startPermissionLoading();
      } else {
        // Keycloak not ready yet, check again shortly
        setTimeout(checkKeycloak, 100);
      }
    };

    // Start checking
    setTimeout(checkKeycloak, 100);

    // Safety timeout: stop checking after 10 seconds
    setTimeout(() => {
      console.debug('[AppInit] Keycloak ready check timeout');
    }, 10000);
  }

  // Return immediately - don't block app bootstrap
  return Promise.resolve();
}

/**
 * Suppress harmless Keycloak silent SSO storage access errors
 * These errors occur when Keycloak tries to use storage in an iframe
 * They are harmless and don't affect functionality
 */
export function suppressKeycloakStorageErrors() {
  return () => {
    // Suppress requestStorageAccess errors from Keycloak silent SSO
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      const message = args[0]?.toString() || '';
      // Suppress requestStorageAccess errors (harmless Keycloak silent SSO errors)
      if (
        message.includes('requestStorageAccess') ||
        message.includes('Permission denied') ||
        message.includes('Must be handling a user gesture')
      ) {
        // Silently ignore these errors - they're harmless
        return;
      }
      // Call original console.error for all other errors
      originalConsoleError.apply(console, args);
    };

    // Suppress window error events for requestStorageAccess
    const originalErrorHandler = globalThis.window.onerror;
    globalThis.window.onerror = (message, source, lineno, colno, error) => {
      const errorMessage =
        typeof message === 'string'
          ? message
          : error?.message || error?.toString() || '';
      // Suppress requestStorageAccess errors
      if (
        errorMessage.includes('requestStorageAccess') ||
        errorMessage.includes('Permission denied') ||
        errorMessage.includes('Must be handling a user gesture')
      ) {
        // Silently ignore these errors
        return true; // Return true to prevent default error handling
      }
      // Call original error handler for other errors
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    // Suppress unhandled promise rejections for requestStorageAccess
    // Note: addEventListener doesn't replace existing handlers, so original handlers are preserved
    globalThis.window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason?.toString() || '';
      // Suppress requestStorageAccess errors in promise rejections
      if (
        reason.includes('requestStorageAccess') ||
        reason.includes('Permission denied') ||
        reason.includes('Must be handling a user gesture')
      ) {
        event.preventDefault(); // Prevent default error handling
      }
      // Other unhandled rejections will propagate normally
    });
  };
}

export function provideCore({ routes }: CoreOptions) {
  return [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    // provideKeycloakAngular(),
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      fallbackLang: 'vi',
    }),
    LanguageInitService,
    PrimeNGTranslationService,
    provideAppInitializer(initializeLanguage),
    provideAppInitializer(initializePrimeNGTranslations),
    provideAppInitializer(suppressKeycloakStorageErrors()),
    provideAppInitializer(() => initializeKeycloak()()),
    provideAppInitializer(initPermissions),
    DialogService,
    MessageService,
    ConfirmationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    provideGoodsApi(environment.goodsServiceBasePath),
    provideAuthApi({
      basePath: environment.authServiceBasePath,
      credentials: {
        BearerAuthentication: () => getKeycloakInstance()?.token || '',
      },
    }),
    provideGoodsApi2(environment.goodsServiceBasePath),
    provideOrderApi(environment.orderServiceBasePath),
    provideStockApi(environment.stockServiceBasePath),
    provideChannelApi(environment.channelServiceBasePath),
    providePromotionApi(environment.promotionServiceBasePath),
    providePosApi(environment.posServiceBasePath),
    providePaymentApi(environment.paymentServiceBasePath),
    providePurchaseApi(environment.purchaseServiceBasePath),
    provideVendorApi(environment.vendorServiceBasePath),
    provideUserApi(environment.userServiceBasePath),
    provideAfterSalesApi(environment.afterSalesServiceBasePath),
    provideEinvoiceApi(environment.einvoiceServiceBasePath),
    providePriceApi(environment.priceServiceBasePath),
  ];
}
